import { useEffect, useState } from "react";
import { http, fmtEUR, RECEIPT_TYPE_LABELS } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResultViewer } from "@/pages/POSRetail";
import { RefreshCw, Trash2, Printer, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirmDialog";

export default function History() {
  // Feb 2026: dual-source history. «Ταμειακή» = fiscal receipts (existing
  // /receipts endpoint). «Εκτυπωτές» = kitchen / bar / delivery print
  // jobs (/print-jobs?pending=false) — the operator uses this to resend
  // a lost ticket to the kitchen printer.
  const [source, setSource] = useState("receipts"); // "receipts" | "print_jobs"
  const [list, setList] = useState([]);
  const [filterType, setFilterType] = useState("all");
  // Feb 2026: quick filter for the «Εκτυπωτές» tab — only show jobs
  // that failed OR are still pending after ≥1 retry. Helps the kitchen
  // manager spot lost tickets without scrolling through printed rows.
  const [printJobsFailedOnly, setPrintJobsFailedOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  // Feb 2026: reprint / resend flow per-row.  When the operator taps
  // the action button we snapshot the receipt into ``actionDlg`` and
  // show a confirmation dialog before firing the fiscal command.
  const [actionDlg, setActionDlg] = useState({ open: false, receipt: null, busy: false });

  const load = async () => {
    if (source === "print_jobs") {
      const r = await http.get("/print-jobs", { params: { pending: false, limit: 300 } });
      let jobs = r.data || [];
      if (printJobsFailedOnly) {
        // Failed = anything that never reached ``printed`` OR needed a
        // retry. This catches both stuck-pending and hard-failure rows.
        jobs = jobs.filter((j) => j.status !== "printed" || (j.retry_count || 0) > 0);
      }
      setList(jobs);
    } else {
      const r = await http.get("/receipts", {
        params: { limit: 300, type: filterType === "all" ? undefined : filterType },
      });
      setList(r.data);
    }
  };
  useEffect(() => {
    load();
  }, [filterType, source, printJobsFailedOnly]);

  const clearAll = async () => {
    if (!(await confirmDialog("Διαγραφή όλου του ιστορικού;"))) return;
    await http.delete("/receipts");
    toast.success("Διαγράφηκε");
    load();
  };

  /**
   * A receipt "went to fiscal" only when the register returned a
   * receipt_counter (device day counter, present after DOC_ISSUE) AND
   * the send succeeded. Kitchen tickets, non-fiscal orders and failed
   * sends have no counter → they must be re-sent, not reprinted.
   *
   * Rows whose ``type`` is already ``doc_reprint`` are excluded from
   * the reprint action — reprinting a reprint would emit «Αντίγραφο
   * αντιγράφου» which the register accepts but is confusing on the
   * counter trail; those rows fall back to «Αποστολή» so the operator
   * can still resend the raw command_text if needed.
   */
  const isFiscal = (r) =>
    Boolean(r?.success && r?.receipt_counter && r?.type !== "doc_reprint");

  const runAction = async () => {
    const r = actionDlg.receipt;
    if (!r) return;
    setActionDlg((s) => ({ ...s, busy: true }));
    try {
      if (source === "print_jobs") {
        // Clone the print job as a fresh pending row — the Companion
        // poller picks it up on the next tick and re-prints on the
        // same printer route as the original.
        const resp = await http.post(`/print-jobs/${r.id}/reprint`);
        if (resp.data?.id) toast.success(`Στάλθηκε ξανά στον εκτυπωτή «${r.windows_printer_name || r.kind}»`);
        else toast.error("Αποτυχία επαναποστολής");
      } else if (isFiscal(r)) {
        // DOC_REPRINT — prints an «Αντίγραφο Φορολογικής Απόδειξης»
        // for the given day counter. z_report null = current day.
        const resp = await http.post("/ftext/doc-reprint", {
          z_report: null,
          day_counter: Number(r.receipt_counter),
        });
        if (resp.data?.ok) toast.success(`Αντίγραφο απόδειξης #${r.receipt_counter}`);
        else toast.error("Αποτυχία επανεκτύπωσης");
      } else {
        // Resend the original command_text as-is through /ftext/raw.
        if (!r.command_text) {
          toast.error("Δεν υπάρχει αποθηκευμένη εντολή για επαναποστολή.");
          return;
        }
        const resp = await http.post("/ftext/raw", {
          command_text: r.command_text,
          station_id: r.station_id || null,
        });
        if (resp.data?.ok) toast.success("Στάλθηκε στην ταμειακή");
        else toast.error(resp.data?.result?.result_description || "Αποτυχία αποστολής");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setActionDlg({ open: false, receipt: null, busy: false });
    }
  };

  const types = ["all", ...Object.keys(RECEIPT_TYPE_LABELS)];

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-grid">
        <div>
          <h2 className="font-heading text-2xl font-bold">Ιστορικό {source === "print_jobs" ? "Εκτυπώσεων" : "Αποδείξεων"}</h2>
          <div className="text-xs text-slate-500">
            {source === "print_jobs"
              ? "Εκτυπώσεις προς κουζίνα / bar / delivery — επανάληψη με ένα κλικ αν χάθηκε το ticket."
              : "Όλες οι εντολές προς την ταμειακή και τα αποτελέσματα."}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {/* Source segmented toggle */}
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-white">
            <button
              type="button"
              className={`px-3 py-1.5 text-sm font-bold transition-colors ${
                source === "receipts"
                  ? "bg-slate-800 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              onClick={() => setSource("receipts")}
              data-testid="history-source-receipts"
            >
              Ταμειακή
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-sm font-bold transition-colors border-l border-slate-200 ${
                source === "print_jobs"
                  ? "bg-slate-800 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              onClick={() => setSource("print_jobs")}
              data-testid="history-source-print-jobs"
            >
              Εκτυπωτές
            </button>
          </div>
          {source === "print_jobs" && (
            <Button
              variant={printJobsFailedOnly ? "default" : "outline"}
              onClick={() => setPrintJobsFailedOnly((v) => !v)}
              className={printJobsFailedOnly ? "bg-red-600 hover:bg-red-700" : "text-red-600 border-red-200"}
              data-testid="history-failed-only-toggle"
              title="Δείξε μόνο εκτυπώσεις που απέτυχαν ή περίμεναν retry"
            >
              <AlertTriangle size={14} className="mr-1.5" />
              {printJobsFailedOnly ? "Όλες" : "Μόνο αποτυχημένα"}
            </Button>
          )}
          {source === "receipts" && (
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-56" data-testid="history-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλοι οι τύποι</SelectItem>
                {Object.entries(RECEIPT_TYPE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={load} data-testid="history-refresh">
            <RefreshCw size={14} className="mr-1.5" />
            Ανανέωση
          </Button>
          {source === "receipts" && (
            <Button
              variant="outline"
              onClick={clearAll}
              className="text-red-600 border-red-200"
              data-testid="history-clear"
            >
              <Trash2 size={14} className="mr-1.5" />
              Καθαρισμός
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6">
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50">
              {source === "print_jobs" ? (
                <TableRow>
                  <TableHead>Ημ/νία</TableHead>
                  <TableHead>Είδος</TableHead>
                  <TableHead>Εκτυπωτής</TableHead>
                  <TableHead>Τραπέζι</TableHead>
                  <TableHead>Κατάσταση</TableHead>
                  <TableHead>Retry</TableHead>
                  <TableHead>Τελ. Σφάλμα</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              ) : (
                <TableRow>
                  <TableHead>Ημ/νία</TableHead>
                  <TableHead>Τύπος</TableHead>
                  <TableHead>Αποτέλεσμα</TableHead>
                  <TableHead>#Απόδ.</TableHead>
                  <TableHead>S/N</TableHead>
                  <TableHead>Σύνολο</TableHead>
                  <TableHead>Χειριστής</TableHead>
                  <TableHead>Σταθμός</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {source === "print_jobs" && list.map((j) => (
                <TableRow
                  key={j.id}
                  data-testid={`history-row-${j.id}`}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelected(j)}
                >
                  <TableCell className="text-xs font-mono tabular">
                    {new Date(j.created_at).toLocaleString("el-GR")}
                  </TableCell>
                  <TableCell className="text-xs uppercase font-bold">{j.kind || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{j.windows_printer_name || "—"}</TableCell>
                  <TableCell className="text-xs">{j.table_name || "—"}</TableCell>
                  <TableCell>
                    {j.status === "printed" ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Εκτυπώθηκε</Badge>
                    ) : j.status === "pending" ? (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200">Εκκρεμεί</Badge>
                    ) : j.status === "claimed" ? (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">Σε εκτύπωση</Badge>
                    ) : (
                      <Badge variant="outline">{j.status || "—"}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs font-mono">{j.retry_count || 0}</TableCell>
                  <TableCell className="text-xs text-red-600 truncate max-w-[220px]">{j.last_error || "—"}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 border-indigo-300 text-indigo-800 hover:bg-indigo-50"
                      onClick={() => setActionDlg({ open: true, receipt: j, busy: false })}
                      data-testid={`history-print-resend-${j.id}`}
                      title="Επανάληψη εκτύπωσης — δημιουργεί νέο pending job"
                    >
                      <Printer size={14} className="mr-1.5" />
                      Επανάληψη
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {source === "receipts" && list.map((r) => (
                <TableRow
                  key={r.id}
                  data-testid={`history-row-${r.id}`}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelected(r)}
                >
                  <TableCell className="text-xs font-mono tabular">
                    {new Date(r.sent_at).toLocaleString("el-GR")}
                  </TableCell>
                  <TableCell className="text-xs">
                    {RECEIPT_TYPE_LABELS[r.type] || r.type}
                  </TableCell>
                  <TableCell>
                    {r.success ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">OK</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 border-red-200">
                        ERR {r.result_code}
                      </Badge>
                    )}
                    {r.simulator && (
                      <Badge variant="outline" className="ml-1 text-[10px]">SIM</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs font-mono">{r.receipt_counter || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{r.serial_number || "—"}</TableCell>
                  <TableCell className="text-xs font-mono tabular">
                    {r.total_amount != null ? fmtEUR(r.total_amount) : "—"}
                  </TableCell>
                  <TableCell className="text-xs" data-testid={`history-user-${r.id}`}>
                    {r.user_name || "—"}
                  </TableCell>
                  <TableCell className="text-xs">{r.station_name || "—"}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                    {isFiscal(r) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                        onClick={() => setActionDlg({ open: true, receipt: r, busy: false })}
                        data-testid={`history-reprint-${r.id}`}
                        title="Επανεκτύπωση αντιγράφου από την ταμειακή"
                      >
                        <Printer size={14} className="mr-1.5" />
                        Αντίγραφο
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-amber-300 text-amber-800 hover:bg-amber-50"
                        onClick={() => setActionDlg({ open: true, receipt: r, busy: false })}
                        data-testid={`history-resend-${r.id}`}
                        title="Αποστολή της παραγγελίας στην ταμειακή"
                      >
                        <Send size={14} className="mr-1.5" />
                        Αποστολή
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-slate-400 py-8">
                    Δεν υπάρχουν εγγραφές
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {selected && (
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-3xl" data-testid="history-detail-dialog">
            <DialogHeader>
              <DialogTitle>
                {RECEIPT_TYPE_LABELS[selected.type] || selected.type}
              </DialogTitle>
            </DialogHeader>
            <ResultViewer log={selected} />
          </DialogContent>
        </Dialog>
      )}

      {/* Action confirmation — Αντίγραφο (fiscal reprint) ή Αποστολή
          (resend raw command_text). Δείχνουμε ξεκάθαρα το receipt
          counter / τον τύπο ώστε ο χειριστής να καταλάβει τι θα σταλεί. */}
      <Dialog
        open={actionDlg.open}
        onOpenChange={(o) => !actionDlg.busy && setActionDlg({ open: o, receipt: actionDlg.receipt, busy: false })}
      >
        <DialogContent className="max-w-md" data-testid="history-action-dialog">
          {actionDlg.receipt && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {source === "print_jobs" ? (
                    <>
                      <Printer size={18} className="text-indigo-700" />
                      Επανάληψη Εκτύπωσης
                    </>
                  ) : isFiscal(actionDlg.receipt) ? (
                    <>
                      <Printer size={18} className="text-emerald-700" />
                      Επανεκτύπωση Αντιγράφου
                    </>
                  ) : (
                    <>
                      <Send size={18} className="text-amber-700" />
                      Αποστολή στην Ταμειακή
                    </>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-1">
                {source === "print_jobs" ? (
                  <div className="rounded-md border bg-slate-50 p-3 text-sm space-y-1">
                    <div>
                      <span className="text-slate-500">Είδος:</span>{" "}
                      <b className="uppercase">{actionDlg.receipt.kind || "—"}</b>
                    </div>
                    <div>
                      <span className="text-slate-500">Εκτυπωτής:</span>{" "}
                      <b className="font-mono">{actionDlg.receipt.windows_printer_name || "—"}</b>
                    </div>
                    {actionDlg.receipt.table_name && (
                      <div>
                        <span className="text-slate-500">Τραπέζι:</span>{" "}
                        <b>{actionDlg.receipt.table_name}</b>
                      </div>
                    )}
                    <div>
                      <span className="text-slate-500">Δημιουργήθηκε:</span>{" "}
                      <span className="font-mono">
                        {new Date(actionDlg.receipt.created_at).toLocaleString("el-GR")}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border bg-slate-50 p-3 text-sm space-y-1">
                    <div>
                      <span className="text-slate-500">Τύπος:</span>{" "}
                      <b>{RECEIPT_TYPE_LABELS[actionDlg.receipt.type] || actionDlg.receipt.type}</b>
                    </div>
                    <div>
                      <span className="text-slate-500">Ημερομηνία:</span>{" "}
                      <span className="font-mono">
                        {new Date(actionDlg.receipt.sent_at).toLocaleString("el-GR")}
                      </span>
                    </div>
                    {actionDlg.receipt.total_amount != null && (
                      <div>
                        <span className="text-slate-500">Σύνολο:</span>{" "}
                        <b className="font-mono">{fmtEUR(actionDlg.receipt.total_amount)}</b>
                      </div>
                    )}
                    {isFiscal(actionDlg.receipt) && (
                      <div>
                        <span className="text-slate-500">Α/Α Απόδειξης:</span>{" "}
                        <b className="font-mono">{actionDlg.receipt.receipt_counter}</b>
                      </div>
                    )}
                  </div>
                )}
                {source === "print_jobs" ? (
                  <div className="text-xs text-indigo-900 bg-indigo-50 border border-indigo-200 rounded-md p-2">
                    Δημιουργείται νέα εντολή εκτύπωσης προς τον ίδιο εκτυπωτή. Χρήσιμο
                    όταν χάθηκε το ticket στην κουζίνα / bar.
                  </div>
                ) : isFiscal(actionDlg.receipt) ? (
                  <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2">
                    Θα εκδοθεί «Αντίγραφο Φορολογικής Απόδειξης» για την Α/Α{" "}
                    <b>#{actionDlg.receipt.receipt_counter}</b> της τρέχουσας ημέρας. Δεν
                    επηρεάζει τα Z/X counters.
                  </div>
                ) : (
                  <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                    <div>
                      Η αρχική εντολή θα ξανασταλεί στην ταμειακή. Αν πρόκειται για
                      αποτυχημένη αποστολή, εξέτασε τη λεπτομέρεια πρώτα ώστε να μη
                      διπλογραφεί απόδειξη.
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setActionDlg({ open: false, receipt: null, busy: false })}
                  disabled={actionDlg.busy}
                  data-testid="history-action-cancel"
                >
                  Άκυρο
                </Button>
                <Button
                  onClick={runAction}
                  disabled={actionDlg.busy}
                  className={
                    source === "print_jobs"
                      ? "bg-indigo-600 hover:bg-indigo-700"
                      : isFiscal(actionDlg.receipt)
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  }
                  data-testid="history-action-confirm"
                >
                  {actionDlg.busy
                    ? "…"
                    : source === "print_jobs"
                      ? "Επανάληψη Εκτύπωσης"
                      : isFiscal(actionDlg.receipt)
                        ? "Εκτύπωση Αντιγράφου"
                        : "Αποστολή"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
